import { Box, Button, Card, Center, Checkbox, Container, Group, Loader, Stack, Text, Title, Alert } from '@mantine/core';
import { IconShieldCheck, IconAlertCircle } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { acceptTerms, saveSessionExpiry } from '../api/auth';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { usePendingSession } from '../hooks/usePendingSession';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderMarkdownToHtml(content: string): string {
  return content
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const heading = block.match(/^(#{1,3})\s+(.+)$/);
      if (heading) {
        const level = heading[1].length;
        return `<h${level}>${escapeHtml(heading[2])}</h${level}>`;
      }

      const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
      if (lines.every((line) => /^[-•]\s+/.test(line))) {
        const items = lines
          .map((line) => `<li>${escapeHtml(line.replace(/^[-•]\s+/, ''))}</li>`)
          .join('\n');
        return `<ul>${items}</ul>`;
      }

      if (lines.every((line) => /^\d+\.\s+/.test(line))) {
        const firstNumber = Number.parseInt(lines[0], 10);
        const start = firstNumber === 1 ? '' : ` start="${firstNumber}"`;
        const items = lines
          .map((line) => `<li>${escapeHtml(line.replace(/^\d+\.\s+/, ''))}</li>`)
          .join('\n');
        return `<ol${start}>${items}</ol>`;
      }

      return `<p>${lines.map(escapeHtml).join('<br />')}</p>`;
    })
    .join('\n\n');
}

export default function AuthTermsPage() {
  const { data: config, isLoading: configLoading, sessionScopeKey } = useResolvedProgram();
  const pendingSessionQuery = usePendingSession(sessionScopeKey);
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [termsHtml, setTermsHtml] = useState<string>('');
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);
  const [termsLoadError, setTermsLoadError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get the returnTo path from query params or location state, or default to home
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo') 
    || (location.state as { returnTo?: string })?.returnTo 
    || '/';

  const termsConfig = config?.termsAndConditions;
  const sessionEmail = pendingSessionQuery.data?.email;
  const expiresAt = pendingSessionQuery.data?.expiresAt;
  const requiredTermsVersion = pendingSessionQuery.data?.requiredTermsVersion;

  // Check for version mismatch (TOCTOU protection)
  const versionMismatch = requiredTermsVersion && termsConfig?.version && 
    requiredTermsVersion !== termsConfig.version;

  // Fetch terms HTML content
  useEffect(() => {
    if (!termsConfig?.contentPath) {
      setIsLoadingTerms(false);
      setTermsLoadError('Terms and Conditions content is not configured at this time.')
      return;
    }

    setIsLoadingTerms(true);
    setTermsLoadError(null);
    setTermsHtml('');

    fetch(termsConfig.contentPath)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load terms: ${res.status}`);
        }

        return res.text();
      })
      .then((content) => {
        if (termsConfig.contentPath.endsWith('.md')) {
          setTermsHtml(renderMarkdownToHtml(content));
        } else {
          setTermsHtml(content);
        }
      })
      .catch((err) => {
        console.error('Failed to load terms:', err);
        setTermsLoadError(
          'Unable to load the Terms and Conditions. Please try again later.'
        );
      })
      .finally(() => {
        setIsLoadingTerms(false);
      });
  }, [termsConfig?.contentPath]);

  // Check if session is expired
  useEffect(() => {
    if (!expiresAt) return;

    const checkExpiry = () => {
      const now = Math.floor(Date.now() / 1000);
      if (now >= expiresAt) {
        setIsExpired(true);
      }
    };

    // Check immediately
    checkExpiry();

    // Check every second
    const interval = setInterval(checkExpiry, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (configLoading || pendingSessionQuery.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // Not authenticated - redirect to login
  if (pendingSessionQuery.isUnauthorized || !sessionEmail) {
    return <Navigate to="/auth/request" state={{ returnTo: location.pathname }} replace />;
  }

  // Version mismatch - terms changed since token was issued
  if (versionMismatch) {
    return (
      <Container size="md" style={{ marginTop: '100px' }}>
        <Alert color={config?.theme.primaryColor} title="Terms and Conditions Version Changed" icon={<IconAlertCircle />}>
          <Stack gap="md">
            <Text>
              The Terms and Conditions have been updated since your sign-in link was issued.
              Please request a new sign-in link to see and accept the current terms.
            </Text>
            <Button size="md" color={config?.theme.primaryColor} onClick={() => navigate('/auth/request')}>
              Request New Sign-In Link
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  if (!termsConfig || !termsConfig.enabled) {
    // T&C not configured, redirect to return path
    return <Navigate to={returnTo} replace />;
  }

  const handleAccept = async () => {
    if (isLoadingTerms || termsLoadError !== null || !termsHtml) {
      return;
    }

    if (isExpired) {
      setError('Your session has expired. Please request a new sign-in link.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await acceptTerms({
        clientName: config!.clientName,
        email: sessionEmail,
        version: termsConfig.version,
      });
      
      if (sessionScopeKey) {
        saveSessionExpiry(sessionScopeKey, result.expiresAt);
      }
      
      navigate(returnTo);
    } catch (error) {
      console.error('Failed to record terms acceptance:', error);
      
      // Check if it's a version change error (409 Conflict)
      if (error instanceof Error && error.message.includes('terms_version_changed')) {
        setError('Terms version has changed. Please request a new sign-in link.');
      } else {
        setError('Failed to record acceptance. Please try again.');
      }
      
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    // Clear any session and redirect to login
    navigate('/auth/request');
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: termsConfig?.backgroundColor || '#f5f5f5',
        paddingTop: '40px',
        paddingBottom: '40px',
      }}
    >
      <Container size="lg">
        <Stack gap="xl">
          {/* Header */}
          <Card shadow="sm" padding="lg" withBorder>
            <Group>
              <IconShieldCheck size={32} color={config?.theme.primaryColor} />
              <div>
                <Title order={2} c="black">{termsConfig.title || config?.theme.displayName}</Title>
                {/* <Text size="sm" c="dimmed">Terms and Conditions</Text> */}
              </div>
            </Group>
          </Card>

          {/* Terms Content */}
          <Card shadow="sm" padding="lg" withBorder>
            <Box>
              {isLoadingTerms ? (
                <Center h={300}>
                  <Stack align="center" gap="sm">
                    <Loader size="lg" />
                    <Text c="dimmed">Loading terms...</Text>
                  </Stack>
                </Center>
              ) : termsLoadError ? (
                <Alert color="red" title="Unable to Load Terms" icon={<IconAlertCircle />}>
                  {termsLoadError}
                </Alert>
              ) : termsHtml ? (
                <iframe
                  title="Terms and Conditions"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <meta charset="utf-8">
                      <style>
                        body {
                          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                          line-height: 1.6;
                          color: #2d3748;
                          padding: 24px;
                          margin: 0;
                        }
                        h1 {
                          font-size: 1.5em;
                          font-weight: 600;
                          margin-top: 0;
                          margin-bottom: 1.5em;
                          color: #1a202c;
                        }
                        h2 {
                          font-size: 1.2em;
                          font-weight: 700;
                          margin: 1.75em 0 0.75em;
                          color: #1a202c;
                        }
                        h3 {
                          font-size: 1em;
                          font-weight: 700;
                          margin: 1.25em 0 0.5em;
                          color: #2d3748;
                        }
                        p {
                          margin: 0 0 1em;
                        }
                        ul, ol {
                          margin: 1em 0;
                          padding-left: 1.5em;
                        }
                        li {
                          margin-bottom: 0.5em;
                        }
                        li::marker {
                          color: #4a5568;
                          font-weight: bold;
                        }
                      </style>
                    </head>
                    <body>
                      ${termsHtml}
                    </body>
                    </html>
                  `}
                  sandbox="allow-same-origin"
                  style={{
                    width: '100%',
                    height: 'min(680px, 65vh)',
                    border: 'none',
                  }}
                />
              ) : null}
            </Box>
          </Card>

          {/* Agreement Checkbox and Actions */}
          <Card shadow="sm" padding="lg" withBorder>
            <Stack gap="md">
              {isExpired && (
                <Alert color="red" title="Session Expired" icon={<IconAlertCircle />}>
                  Your sign-in session has expired. Please request a new sign-in link to continue.
                </Alert>
              )}
              
              <Group gap="md" align="center">
                <Checkbox
                  checked={agreed}
                  onChange={(event) => setAgreed(event.currentTarget.checked)}
                  disabled={isLoadingTerms || termsLoadError !== null || isSubmitting || isExpired}
                  color={config?.theme.primaryColor}
                />
                <Text size="sm" style={{ flex: 1 }}>
                  I have read and agree to the Terms and Conditions
                </Text>
                <Button
                  onClick={handleAccept}
                  disabled={!agreed || isLoadingTerms || termsLoadError !== null || isSubmitting || isExpired}
                  loading={isSubmitting}
                  color={config?.theme.primaryColor}
                >
                  I Accept
                </Button>
              </Group>
              
              {error && (
                <Text size="sm" c="red">
                  {error}
                </Text>
              )}
              
              {isExpired && (
                <Button
                  variant="outline"
                  onClick={handleDecline}
                  disabled={isSubmitting}
                  fullWidth
                >
                  Request New Link
                </Button>
              )}
            </Stack>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
